import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

interface FreeTextChipFieldProps {
  label?: string;
  placeholder: string;
  value: string[];
  onChange: (values: string[]) => void;
  maxItems?: number;
  helperText?: string;
  errorMessage?: string;
  suggestions?: string[]; // Optional suggestions to show
}

export const FreeTextChipField: React.FC<FreeTextChipFieldProps> = ({
  label,
  placeholder,
  value,
  onChange,
  maxItems = 5,
  helperText,
  errorMessage,
  suggestions = []
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [isSelectingSuggestion, setIsSelectingSuggestion] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input value and exclude already selected items
  useEffect(() => {
    if (inputValue.trim() === '' || suggestions.length === 0) {
      setFilteredSuggestions([]);
    } else {
      const matchingSuggestions = suggestions
        .filter(suggestion => 
          !value.includes(suggestion) && 
          suggestion.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 5);
      setFilteredSuggestions(matchingSuggestions);
    }
  }, [inputValue, suggestions, value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current && 
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setSelectedSuggestionIndex(-1);
  };

  const addChip = (text: string) => {
    const trimmedText = text.trim();
    if (trimmedText && !value.includes(trimmedText) && value.length < maxItems) {
      onChange([...value, trimmedText]);
      setInputValue('');
      setSelectedSuggestionIndex(-1);
    }
  };

  const handleRemoveChip = (removedChip: string) => {
    onChange(value.filter(chip => chip !== removedChip));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && filteredSuggestions.length > 0) {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => (prev < filteredSuggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp' && filteredSuggestions.length > 0) {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : filteredSuggestions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      
      if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < filteredSuggestions.length) {
        addChip(filteredSuggestions[selectedSuggestionIndex]);
      } else if (inputValue.trim() !== '') {
        addChip(inputValue);
      }
    } else if (e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      if (inputValue.trim() !== '') {
        addChip(inputValue);
      }
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      // Remove last chip when backspace is pressed on empty input
      onChange(value.slice(0, -1));
    }
  };

  const handleInputBlur = () => {
    // Don't add input as chip if we're currently selecting a suggestion
    if (!isSelectingSuggestion && inputValue.trim() !== '') {
      addChip(inputValue);
    }
    // Reset the flag after a short delay to ensure suggestion click is processed
    setTimeout(() => {
      setIsSelectingSuggestion(false);
    }, 100);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setIsSelectingSuggestion(true);
    addChip(suggestion);
    // Reset the flag after processing
    setTimeout(() => {
      setIsSelectingSuggestion(false);
    }, 100);
  };

  const handleSuggestionMouseDown = (e: React.MouseEvent) => {
    // Prevent the input from losing focus when clicking on suggestions
    e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const items = pastedText.split(',').map(item => item.trim()).filter(Boolean);
    
    const newItems = items.filter(item => !value.includes(item));
    const availableSlots = maxItems - value.length;
    const itemsToAdd = newItems.slice(0, availableSlots);
    
    if (itemsToAdd.length > 0) {
      onChange([...value, ...itemsToAdd]);
    }
    setInputValue('');
  };

  return (
    <div className="w-full mb-4">
      {label && (
        <label 
          htmlFor={`freetext-${label}`} 
          className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1"
        >
          {label}
        </label>
      )}
      
      <div className="relative">
        <div className={`
          flex flex-wrap gap-2 p-3 bg-surface-light dark:bg-surface-dark 
          border ${errorMessage ? 'border-error-light dark:border-error-dark' : 'border-border-light dark:border-border-dark'} 
          rounded-lg focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent 
          transition-all min-h-[56px]
        `}>
          {value.map((chip, index) => (
            <div key={`${chip}-${index}`} className="inline-flex items-center px-2.5 py-1 rounded-full text-sm bg-primary-light/10 dark:bg-primary-dark/20 text-primary-light dark:text-primary-dark">
              <span className="mr-1 font-medium">{chip}</span>
              <button
                type="button"
                onClick={() => handleRemoveChip(chip)}
                className="p-0.5 rounded-full hover:bg-primary-light/20 dark:hover:bg-primary-dark/30 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          
          <input
            id={`freetext-${label}`}
            ref={inputRef}
            type="text"
            className={`text-sm
              flex-grow min-w-[120px] bg-transparent border-none p-0 focus:outline-none
              text-text-primary-light dark:text-text-primary-dark
              placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark
            `}
            placeholder={value.length === 0 ? placeholder : value.length >= maxItems ? 'Maximum items reached' : 'Add more...'}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={value.length >= maxItems}
          />
        </div>
        
        {/* Suggestions dropdown */}
        {isFocused && filteredSuggestions.length > 0 && (
          <div 
            ref={dropdownRef}
            className="absolute z-50 w-full mt-1 bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg max-h-60 overflow-auto"
          >
            <ul className="py-1">
              {filteredSuggestions.map((suggestion, index) => (
                <li
                  key={index}
                  className={`px-3 py-2 text-sm cursor-pointer transition-colors duration-150 ${
                    index === selectedSuggestionIndex
                      ? 'bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark'
                      : 'text-text-primary-light dark:text-text-primary-dark hover:bg-background-light/50 dark:hover:bg-background-dark/50'
                  }`}
                  onMouseDown={handleSuggestionMouseDown}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      
      {helperText && !errorMessage && (
        <p className="mt-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
          {helperText}
        </p>
      )}
      
      {errorMessage && (
        <p className="mt-1 text-xs text-error-light dark:text-error-dark">
          {errorMessage}
        </p>
      )}
    </div>
  );
}; 